import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Tracks whether this browser is signed in as the admin.
// The real check happens on the server for every admin action. This only decides
// what to show, so hiding a button here is never the only protection.

const AdminContext = createContext({
  isAdmin: false,
  checked: false,
  login: async () => ({ ok: false }),
  logout: async () => {},
  expire: () => {},
});

export function AdminProvider({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/session', { cache: 'no-store' });
      const data = await res.json();
      setIsAdmin(Boolean(data.admin));
    } catch {
      setIsAdmin(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (password) => {
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setIsAdmin(true);
        return { ok: true };
      }
      return { ok: false, error: data.error || 'Sign in failed. Try again.' };
    } catch {
      return { ok: false, error: 'Could not reach the server. Check your connection.' };
    }
  };

  const logout = async () => {
    await fetch('/api/admin/logout', { method: 'POST' }).catch(() => {});
    setIsAdmin(false);
  };

  // Called when the server rejects an admin action, for example after the session expires.
  const expire = useCallback(() => setIsAdmin(false), []);

  return (
    <AdminContext.Provider value={{ isAdmin, checked, login, logout, expire }}>
      {children}
    </AdminContext.Provider>
  );
}

export const useAdmin = () => useContext(AdminContext);

// fetch() for admin actions. Signs the browser out locally if the server says the session is gone.
export async function adminFetch(url, options, expire) {
  const res = await fetch(url, options);
  if (res.status === 401) {
    expire();
    throw new Error('Your admin session has ended. Sign in again.');
  }
  return res.json();
}
